/**
 * Vitrine de servicos do perfil.
 *
 * Mostra o catalogo na ordem definida pela gestao. Cada card leva ao wizard na
 * primeira etapa, sem carregar a escolha (decisao registrada em DEC-2 da spec),
 * entao o rotulo acessivel informa o servico e diz que o toque comeca o
 * agendamento — sem prometer que aquele servico fica selecionado.
 *
 * O preco e o do catalogo. O wizard mostra o valor apurado por cotacao, que
 * coincide com o catalogo quando o estabelecimento nao repassa taxa ao cliente.
 */

import Link from "next/link";
import { FiClock, FiImage } from "react-icons/fi";
import type { PublicService } from "../types";
import { resolveImageUrl } from "../utils";
import { formatBRL } from "../../../lib/currency";
import { formatDurationMinutes } from "../../../lib/time";
import { PreviewCarousel } from "./PreviewCarousel";

interface ProfileServicesPreviewProps {
  services: PublicService[];
  bookingHref: string;
}

function resolvePriceLabel(service: PublicService): string {
  if (!service.showPrice || typeof service.price !== "number") {
    return "Valor no atendimento";
  }
  return service.priceStartingFrom
    ? `A partir de ${formatBRL(service.price)}`
    : formatBRL(service.price);
}

export function ProfileServicesPreview({
  services,
  bookingHref,
}: ProfileServicesPreviewProps) {
  const available = services.filter((service) => service.active !== false);
  if (available.length === 0) return null;

  return (
    <section
      aria-label="Serviços do estabelecimento"
      data-testid="profile-services-preview"
      className="rounded-2xl bg-white px-3 py-4 shadow-sm"
    >
      <div className="mb-1 px-1">
        <h2 className="text-base font-bold text-[#233055]">O que a gente faz</h2>
        <p className="mt-0.5 text-sm text-slate-600">
          Toque em um serviço para começar seu agendamento.
        </p>
      </div>

      <PreviewCarousel
        items={available}
        getKey={(service) => service.id}
        renderItem={(service) => {
          const priceLabel = resolvePriceLabel(service);
          const durationLabel = formatDurationMinutes(service.durationMinutes);
          const imageSrc = service.imageUrl ? resolveImageUrl(service.imageUrl) : "";

          return (
            <Link
              href={bookingHref}
              data-testid={`profile-service-card-${service.id}`}
              aria-label={`${service.name}, ${durationLabel}, ${priceLabel}. Toque para começar o agendamento.`}
              className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-primary-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                {imageSrc ? (
                  <>
                    {/* Fundo desfocado preenche a moldura sem recortar a foto. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full scale-125 object-cover opacity-45 blur-2xl"
                    />
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      className="relative z-10 h-full w-full object-contain"
                    />
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-400">
                    <FiImage className="h-8 w-8" aria-hidden="true" />
                  </div>
                )}
              </div>

              <div className="p-3" aria-hidden="true">
                <p className="line-clamp-2 text-base font-semibold leading-tight text-[#233055]">
                  {service.name}
                </p>
                <div className="mt-1.5 flex items-center justify-between gap-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <FiClock className="h-4 w-4" aria-hidden="true" />
                    {durationLabel}
                  </span>
                  <span className="truncate font-semibold text-primary-700">
                    {priceLabel}
                  </span>
                </div>
              </div>
            </Link>
          );
        }}
        ariaLabel="Serviços em destaque"
        previousLabel="Ver serviço anterior"
        nextLabel="Ver próximo serviço"
        testId="profile-services-carousel"
      />
    </section>
  );
}
